/**
 * AlimTalkContainer
 * --
 */
import React from 'react';
import AlimTalkPresenter from './AlimTalkPresenter';
import { BizApi } from 'api';
import { useInView } from 'react-intersection-observer';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import {
  useBizSendKakaoAi,
  useBizSendKakaoAt,
  useBizTemplateDetail,
  useBizTemplateReplace,
} from 'hooks/BizHooks';
import {
  useCreateAlimTalkTemplate,
  useGetAlimTalkTemplates,
} from 'hooks/AlimTalkHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import { useGetUsers } from 'hooks/UserHooks';

const PAGE_COUNT = '50';

const AlimTalkContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== Data ===== */
  // 템플릿
  const { data: Templates } = useGetAlimTalkTemplates(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  //   console.log('Templates ===>', Templates);

  // 등급 데이터
  const { data: grades } = useGetGrades(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });

  // 회원 데이터
  const { data: users } = useGetUsers(
    {
      join_tables: '["grade"]',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );

  /* ===== Mutate ===== */
  const { bizTemplateDetail } = useBizTemplateDetail();
  const { createAlimTalkTemplate } = useCreateAlimTalkTemplate();
  const { bizTemplateReplace } = useBizTemplateReplace();
  const { sendKakaoAt } = useBizSendKakaoAt();
  const { sendKakaoAi } = useBizSendKakaoAi();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [ref, inView] = useInView();
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFetch, setIsFetch] = React.useState(false);
  // 페이지
  const [page, setPage] = React.useState('1');
  // 데이터
  const [data, setData] = React.useState(null);
  // 상세데이터
  const [detail, setDetail] = React.useState(null);
  // 선택회원
  const [selectedUsers, setSelectedUsers] = React.useState([]);
  // 변수 입력값
  const [inputValue, setInputValue] = React.useState({});

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 무한 스크롤 데이터 호출
   * --
   */
  const fetchData = React.useCallback(async () => {
    try {
      setIsFetch(true);
      const { status, data: listData } = await BizApi.bizTemplateList({
        page: page,
        count: PAGE_COUNT,
      });

      if (status !== 200 || listData?.code !== '200') {
        MessageAlert.info('템플릿을 호출하는데 실패하였습니다.');
        return;
      }

      const list = data?.data?.list ?? [];

      setData((prev) => ({
        ...prev,
        data: {
          hasNext: page + 1 === listData?.currentPage ? false : true,
          list: [...list, ...listData?.data?.list],
        },
      }));
      setPage((prev) => `${Number(prev) + 1}`);
    } catch (err) {
      console.error('[fetchData] Error!! ', err);
      MessageAlert.error('템플릿을 호출하는데 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setIsFetch(false);
      }, 1000);
    }
  }, [inView]);

  /**
   * 템플릿 상세조회
   * --
   */
  const fetchDetailData = React.useCallback(
    async (temp) => {
      let tempData = null;
      try {
        setIsLoading(true);
        const { status, data: detailData } = await bizTemplateDetail({
          templateCode: temp,
        });

        if (status !== 200) {
          MessageAlert.info('템플릿을 호출하는데 실패하였습니다');
          return;
        }

        if (detailData?.code !== '200') {
          MessageAlert.info(detailData?.message);
          return;
        }

        const findTemp = Templates?.find(
          (temp) =>
            temp?.alim_talk_template_code === detailData?.data?.templateCode
        );

        MessageAlert.success(detailData?.message);

        delete detailData?.data['comments'];

        tempData = {
          ...detailData?.data,
          is_register: !!findTemp,
          placeholder: findTemp ? findTemp?.place_holder : null,
          type: findTemp ? findTemp.type : null,
        };
      } catch (err) {
        console.error('[fetchDetailData] Error!! ', err);
        MessageAlert.error('템플릿을 호출하는데 실패하였습니다');
      } finally {
        setTimeout(() => {
          setDetail(tempData);
          setIsLoading(false);
        }, 1000);
      }
    },
    [Templates]
  );

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedUsers((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedUsers((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedUsers(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedUsers.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setSelectedUsers(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedUsers.length === obj.length;

        if (!isAll) {
          setSelectedUsers(obj);
        } else {
          setSelectedUsers([]);
        }
        return;
      default:
        return;
    }
  };

  /**
   * 변수 추출 (중복 제거 포함)
   * --
   * @param {object} data
   * @returns {string[]} #{변수} 형식의 변수들만 리스트로 반환 (중복 제거)
   */
  const extractHashContents = (data) => {
    let result = new Set(); // Set을 사용하여 중복 제거

    const recursiveSearch = (obj) => {
      if (typeof obj === 'object' && obj !== null) {
        Object.values(obj).forEach((value) => {
          if (typeof value === 'string') {
            // 문자열에서 모든 "#{내용}" 패턴 찾기 (문자열 중간 포함)
            const matches = [...value.matchAll(/#\{(.+?)\}/g)];
            matches.forEach((match) => result.add(match[1])); // Set에 저장하여 중복 방지
          } else if (Array.isArray(value)) {
            // 배열이면 각 요소를 재귀적으로 탐색
            value.forEach((item) => recursiveSearch(item));
          } else if (typeof value === 'object') {
            // 객체이면 다시 탐색
            recursiveSearch(value);
          }
        });
      }
    };

    recursiveSearch(data);
    return Array.from(result); // Set을 배열로 변환하여 반환
  };

  /**
   * 메세지 발송 유효성 검사
   * --
   */
  const handleValidateCheck = () => {
    if (!detail) {
      MessageAlert.info('발송할 알림톡을 선택해주세요!');
      return false;
    }

    if (detail?.inspectionStatus !== 'APR') {
      MessageAlert.info('승인되지 않은 템플릿입니다!');
      return false;
    }

    if (!selectedUsers.length) {
      MessageAlert.info('메세지를 발송할 회원을 선택해주세요!');
      return false;
    }

    const placeholder = Object.keys(detail?.placeholder);
    const inputPlace = Object.entries(inputValue)
      .filter(([key, value]) => value && value !== '')
      .reduce((acc, [key, value]) => {
        acc[key] = value;

        return acc;
      }, {});

    if (Object.keys(placeholder).length !== Object.keys(inputPlace).length) {
      MessageAlert.info('모든 변수명을 입력해주세요!');
      return false;
    }

    return true;
  };

  /**
   * 알림톡 메세지 전송
   * --
   */
  const handleSubmit = async () => {
    if (!handleValidateCheck()) return;

    const confirm = window.confirm(
      `${selectedUsers.length}명에게 알림톡을 전송하시겠습니까?`
    );

    if (!confirm) return;

    try {
      setIsLoading(true);

      const replaceTemp = await bizTemplateReplace({
        alim_talk_template_code: detail?.templateCode,
        place_holder: detail?.placeholder,
        replace_value: inputValue,
      });

      if (replaceTemp.status !== 200) {
        MessageAlert.info('알림톡 전송에 실패하였습니다.');
        return;
      }

      const alimeTalkData = {
        to_phones: selectedUsers.map((user) => user.phone),
        ...replaceTemp.data,
        is_template: 'N',
      };

      let send;
      if (replaceTemp.data?.type === 'ai') {
        send = await sendKakaoAi(alimeTalkData);
      } else {
        send = await sendKakaoAt(alimeTalkData);
      }

      if (send.status !== 200) {
        MessageAlert.info('알림톡 전송에 실패하였습니다.');
        return;
      }

      MessageAlert.success(
        `${stringToMoneyFormat(
          send.data.success.length
        )}건 전송 성공, ${stringToMoneyFormat(
          send.data.failure.length
        )}건 전송 실패`
      );
    } catch (err) {
      console.error('[handleSubmit] Error !! ', err);
      MessageAlert.error('알림톡 전송에 실패하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 1000);
    }
  };

  /**
   * 알림톡 템플릿 저장
   * --
   */
  const handleRegister = async () => {
    const confirm = window.confirm('템플릿을 저장하시겠습니까?');

    if (!confirm) return;

    let isSuccess = false;
    try {
      setIsLoading(true);

      // 변수명 추출
      const placeholder = extractHashContents(detail);

      const tempData = {
        alim_talk_title: detail?.templateName,
        alim_talk_description: detail?.templateName,
        alim_talk_template_code: detail?.templateCode,
        place_holder: placeholder.reduce((acc, cur) => {
          acc[cur] = null;

          return acc;
        }, {}),
        is_enable: 'Y',
        user_type: 'ADMIN',
      };

      const { status } = await createAlimTalkTemplate(tempData);

      if (status !== 200) {
        MessageAlert.info('템플릿 저장에  실패하였습니다!');
        return;
      }

      MessageAlert.success('템플릿이 성공적으로 저장되었습니다.');
      isSuccess = true;
    } catch (err) {
      console.error('[handleRegister] Error !! ', err);
      MessageAlert.error('알림톡 템플릿 저장에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        if (isSuccess) {
          window.location.reload();
        }
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 무한 스크롤 데이터 호출
   * --
   */
  React.useEffect(() => {
    if (inView) {
      fetchData();
    }
  }, [inView]);

  /**
   * 등급 데이터 정제
   * --
   */
  const Grades = React.useMemo(() => {
    if (!grades.length) return [];

    const newGrades = grades.map((grade) => ({
      label: grade.grade_name,
      value: grade.grade_id,
    }));

    return newGrades;
  }, [grades]);

  /**
   * 회원 데이터 정제
   * --
   */
  const Users = React.useMemo(() => {
    if (!users.length) return [];

    const newUsers = users.map((user) => {
      const { user_phone, user_setting, grade, user_account, social_id } = user;

      return {
        ...user,
        key: user.user_id,
        phone: user_phone,
        grade_name: grade?.grade_name,
        is_member: user_account || social_id ? 'Y' : 'N',
        push_notification: user_setting?.push_notification ? 'Y' : 'N' ?? 'N', // 푸시알림 동의 여부
        event_notification: user_setting?.event_notification ? 'Y' : 'N' ?? 'N', // 프로모션/이벤트 알림 동의 여부
        marketing_consent: user_setting?.marketing_consent ? 'Y' : 'N' ?? 'N', // 마케팅정보 수집 및 활용 동의 여부
        terms_of_ad: user_setting?.terms_of_ad ? 'Y' : 'N' ?? 'N', // 혜택 및 광고성 정보 수신 동의 여부
        terms_of_marketing: user_setting?.terms_of_marketing ? 'Y' : 'N' ?? 'N', // 마케팅 목적의 개전정보 수집 및 이용 동의 여부
      };
    });

    return newUsers;
  }, [users]);

  /* ===== RENDER ===== */
  return (
    <>
      <AlimTalkPresenter
        scrollRef={ref}
        isFetch={isFetch}
        isLoading={isLoading}
        data={data}
        detail={detail}
        onSelect={fetchDetailData}
        users={Users}
        grades={Grades}
        selectedUsers={selectedUsers}
        onUserSelect={handleSelect}
        setIsLoading={setIsLoading}
        onSubmit={handleSubmit}
        onRegister={handleRegister}
        inputValue={inputValue}
        setInputValue={setInputValue}
      />
    </>
  );
};

export default AlimTalkContainer;
